import * as React from "react";
import { useState } from "react";
import { usePersistentCallback, WidgetRenderer } from "@skbkontur/widget-consumer-react-utils";
import {
    CandyWidgetFeature,
    importWidgetModule,
    inferCandyWidgetUrlFromCandyApiUrl,
    parseGuid,
    WidgetApi,
    ReloadFunction,
} from "@kontur.candy/candy-widget-npm-loader";

import { useAsyncEffect } from "../../../../../Common/UseAsyncEffect";
import { inferCabinetApiUrlFromKeformsUrl } from "../../../Engine/Form/Form";
import { resolveWidgetApiKey } from "../../../Helpers/WidgetUtils";

export interface HistoryEditingProps {
    ns: string;
    draftId: string;
    keformsUrl: string;
    onClose?: () => void;
    onBeginLoad: () => void;
    onCompleteLoad: () => void;
    reload: ReloadFunction;
    isProdEnvironment: boolean;
}

function WidgetContainer({
    widgetApi,
    ns,
    draftId,
    onClose,
    reload,
    isProdEnvironment,
}: {
    widgetApi: WidgetApi;
    ns: string;
    draftId: string;
    onClose: VoidFunction;
    reload: ReloadFunction;
    isProdEnvironment: boolean;
}) {
    const initCallback = usePersistentCallback(
        (container: HTMLElement) =>
            widgetApi.init({
                container: container,
                ns: ns,
                draftId: draftId,
                historyEditingOptions: {
                    onSidePageClose: onClose,
                    reload: reload,
                    isProdEnvironment: isProdEnvironment,
                    readonlyHistory: false,
                },
            }),
        [ns, draftId, widgetApi]
    );

    return (
        <WidgetRenderer
            // @ts-ignore
            renderWidget={initCallback}
        />
    );
}

export function HistoryEditing({
    ns,
    draftId,
    keformsUrl,
    onClose,
    reload,
    isProdEnvironment,
    onBeginLoad,
    onCompleteLoad,
}: HistoryEditingProps) {
    const [widgetApi, setWidgetApi] = useState<WidgetApi | null>(null);

    useAsyncEffect(async () => {
        try {
            onBeginLoad();
            const widgetUrl = inferCandyWidgetUrlFromCandyApiUrl(new URL(keformsUrl));
            const initResponse = await importWidgetModule({
                widgetUrl: widgetUrl,
                apiKey: resolveWidgetApiKey(),
                apiUrl: keformsUrl,
                workspaceId: parseGuid("47fcf184-0600-437a-a19c-d1882779e0d0"),
                resourceZone: undefined,
                widgetFeature: CandyWidgetFeature.HistoryEditing,
                cabinetApiUrl: inferCabinetApiUrlFromKeformsUrl(keformsUrl),
            });
            if (initResponse.success) {
                setWidgetApi(initResponse.value);
            } else {
                onClose?.();
            }
        } finally {
            onCompleteLoad();
        }
    }, [keformsUrl, ns, draftId]);

    return widgetApi != undefined ? (
        <WidgetContainer
            widgetApi={widgetApi}
            ns={ns}
            draftId={draftId}
            onClose={() => onClose?.()}
            reload={reload}
            isProdEnvironment={isProdEnvironment}
        />
    ) : null;
}
